import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, ExternalLink, FileText, Edit3, ShieldCheck } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EUR = (v) => new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(Number(v || 0));

/**
 * Public review page for the proposal pre-validation flow.
 * No auth — the token in the URL is the credential (like /margin-authorization).
 */
export default function ProposalPrevalidationPublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(false);
  const [edits, setEdits] = useState({});
  const [decision, setDecision] = useState(null); // 'approve' | 'changes'
  const [reason, setReason] = useState("");
  const [approvalNote, setApprovalNote] = useState("");
  const [decidedBy, setDecidedBy] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/public/proposal-prevalidation/${token}`);
      setData(r.data);
      setEdits(r.data.editable || {});
    } catch (e) {
      setErr(e.response?.data?.detail || "Link inválido ou expirado.");
    }
  };
  useEffect(() => { load(); }, [token]); // eslint-disable-line

  const submitApprove = async () => {
    setBusy(true);
    try {
      const changed = {};
      const base = data.editable || {};
      Object.keys(edits).forEach((k) => {
        if (edits[k] !== base[k]) changed[k] = edits[k];
      });
      const r = await axios.post(`${API}/public/proposal-prevalidation/${token}/approve`, {
        decided_by_name: decidedBy || "Rui Pinto",
        edits: Object.keys(changed).length ? changed : null,
        approval_note: approvalNote || null,
      });
      toast.success(r.data.edits_applied ? "Proposta aprovada com edições." : "Proposta aprovada.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao aprovar.");
    } finally { setBusy(false); }
  };

  const submitChanges = async () => {
    if (!reason.trim()) { toast.error("Motivo obrigatório."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/public/proposal-prevalidation/${token}/request-changes`, {
        reason: reason.trim(),
        decided_by_name: decidedBy || "Rui Pinto",
      });
      toast.success("Alterações pedidas — o consultor foi notificado.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro.");
    } finally { setBusy(false); }
  };

  if (err) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-8">
        <div className="max-w-md text-center space-y-3">
          <XCircle className="w-10 h-10 mx-auto text-[hsl(var(--danger))]" />
          <h1 className="font-display text-2xl">Não foi possível abrir</h1>
          <p className="text-sm text-muted-foreground">{err}</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  const decided = data.status !== "pending";

  return (
    <div className="min-h-screen bg-background text-foreground py-10 px-6" data-testid="prevalidation-public-page">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2 flex items-center gap-1.5">
            <ShieldCheck className="w-3 h-3" /> Validação Prévia de Proposta
          </div>
          <h1 className="font-display text-3xl lg:text-4xl tracking-tight">{data.lead?.name || "—"}</h1>
          <div className="text-sm text-muted-foreground mt-1">
            {data.lead?.phone} · {data.lead?.email}
          </div>
          <div className="text-[11px] text-muted-foreground mt-2">
            Solicitado por {data.requested_by_name} em {new Date(data.requested_at).toLocaleString("pt-PT")}
          </div>
        </div>

        {/* Decision banner (if already decided) */}
        {decided && data.status === "approved" && (
          <div className="rounded-md border border-[hsl(var(--success))]/40 bg-[hsl(var(--success-bg))] text-[hsl(var(--success))] p-4 flex items-start gap-3" data-testid="prevalidation-decided-approved">
            <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-semibold">Aprovado por {data.decided_by_name || "validador"}</div>
              <div className="text-[13px]">Em {new Date(data.decided_at).toLocaleString("pt-PT")}. O consultor foi notificado e pode agora enviar ao cliente.</div>
              {data.decision_reason && <div className="text-[13px] italic mt-1">"{data.decision_reason}"</div>}
            </div>
          </div>
        )}
        {decided && data.status === "changes_requested" && (
          <div className="rounded-md border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning))] p-4 flex items-start gap-3" data-testid="prevalidation-decided-changes">
            <XCircle className="w-5 h-5 shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-semibold">Alterações pedidas por {data.decided_by_name || "validador"}</div>
              <div className="text-[13px]">Em {new Date(data.decided_at).toLocaleString("pt-PT")}.</div>
              <div className="text-[13px] italic mt-1">"{data.decision_reason}"</div>
            </div>
          </div>
        )}

        {/* PDF link */}
        <div className="rounded-md border border-border bg-card p-5">
          <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold mb-2">Proposta</div>
          <div className="flex flex-wrap gap-3">
            <a
              href={data.proposal_pdf_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-muted hover:bg-accent border border-border text-sm font-semibold"
              data-testid="prevalidation-view-pdf"
            >
              <FileText className="w-4 h-4" /> Abrir PDF da proposta
            </a>
            <a
              href={data.proposal_public_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-sm text-muted-foreground hover:text-foreground"
            >
              <ExternalLink className="w-4 h-4" /> Ver página do cliente
            </a>
          </div>
        </div>

        {/* Editable values */}
        <div className="rounded-md border border-border bg-card p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Valores-chave</div>
            {!decided && !editing && (
              <button
                onClick={() => setEditing(true)}
                className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                data-testid="prevalidation-edit-toggle"
              >
                <Edit3 className="w-3 h-3" /> Editar
              </button>
            )}
          </div>
          {!editing ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <Kv label="Mensalidade min" value={EUR(edits.monthly_price_min)} />
              <Kv label="Mensalidade máx" value={EUR(edits.monthly_price_max)} />
              <Kv label="Arranque de recrutamento" value={EUR(edits.kickstart_amount)} />
              <Kv label="Caução total" value={EUR(edits.total_amount)} />
              <Kv label="Data de arranque" value={edits.service_start_date || "—"} />
              <div className="col-span-2 md:col-span-3">
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Serviço recomendado</div>
                <div className="text-sm mt-0.5">{edits.recommended_service || "—"}</div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <NumInput label="Mensalidade min (€)" v={edits.monthly_price_min} onChange={(v) => setEdits((e) => ({ ...e, monthly_price_min: v }))} testid="edit-monthly-min" />
              <NumInput label="Mensalidade máx (€)" v={edits.monthly_price_max} onChange={(v) => setEdits((e) => ({ ...e, monthly_price_max: v }))} testid="edit-monthly-max" />
              <NumInput label="Arranque de recrutamento (€)" v={edits.kickstart_amount} onChange={(v) => setEdits((e) => ({ ...e, kickstart_amount: v }))} testid="edit-kickstart" />
              <NumInput label="Caução total (€)" v={edits.total_amount} onChange={(v) => setEdits((e) => ({ ...e, total_amount: v }))} testid="edit-total" />
              <div>
                <label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Data de arranque</label>
                <input type="date" value={edits.service_start_date || ""} onChange={(e) => setEdits((s) => ({ ...s, service_start_date: e.target.value }))}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm mt-1" data-testid="edit-start-date" />
              </div>
              <div className="md:col-span-2">
                <label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Serviço recomendado</label>
                <input type="text" value={edits.recommended_service || ""} onChange={(e) => setEdits((s) => ({ ...s, recommended_service: e.target.value }))}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm mt-1" data-testid="edit-service" />
              </div>
              <div className="md:col-span-2">
                <label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Condições comerciais</label>
                <textarea value={edits.commercial_terms || ""} onChange={(e) => setEdits((s) => ({ ...s, commercial_terms: e.target.value }))}
                  rows={4} className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm mt-1" data-testid="edit-terms" />
              </div>
            </div>
          )}
        </div>

        {/* Decision panel (pending only) */}
        {!decided && (
          <div className="rounded-md border border-border bg-card p-5 space-y-4">
            <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Decisão</div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">O seu nome (opcional)</label>
              <input type="text" value={decidedBy} onChange={(e) => setDecidedBy(e.target.value)}
                placeholder="Ex.: Rui Pinto"
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm mt-1"
                data-testid="prevalidation-decided-by" />
            </div>

            {decision === null && (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setDecision("approve")}
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/90 text-white text-sm font-semibold"
                  data-testid="prevalidation-btn-approve"
                >
                  <CheckCircle2 className="w-4 h-4" /> Aprovar {editing ? "com edições" : ""}
                </button>
                <button
                  onClick={() => setDecision("changes")}
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-[hsl(var(--warning))] hover:bg-[hsl(var(--warning))]/90 text-white text-sm font-semibold"
                  data-testid="prevalidation-btn-changes"
                >
                  <XCircle className="w-4 h-4" /> Pedir alterações
                </button>
              </div>
            )}

            {decision === "approve" && (
              <div className="space-y-3">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Nota de aprovação (opcional)</label>
                  <textarea value={approvalNote} onChange={(e) => setApprovalNote(e.target.value)} rows={3}
                    placeholder="Ex.: OK, com margem apertada mas aceitável dado o volume."
                    className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm mt-1"
                    data-testid="prevalidation-approval-note" />
                </div>
                <div className="flex gap-2">
                  <button disabled={busy} onClick={submitApprove}
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/90 text-white text-sm font-semibold disabled:opacity-50"
                    data-testid="prevalidation-approve-submit">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    Confirmar aprovação
                  </button>
                  <button onClick={() => setDecision(null)} className="h-10 px-3 rounded-md text-sm text-muted-foreground hover:text-foreground">Cancelar</button>
                </div>
              </div>
            )}

            {decision === "changes" && (
              <div className="space-y-3">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Motivo das alterações *</label>
                  <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={4}
                    placeholder="Explica ao consultor o que precisa de mudar (ex.: caução kickstart demasiado baixa; renegociar mensalidade; incluir cláusula de renovação)…"
                    className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm mt-1"
                    data-testid="prevalidation-reason" />
                </div>
                <div className="flex gap-2">
                  <button disabled={busy || !reason.trim()} onClick={submitChanges}
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-[hsl(var(--warning))] hover:bg-[hsl(var(--warning))]/90 text-white text-sm font-semibold disabled:opacity-50"
                    data-testid="prevalidation-changes-submit">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                    Enviar pedido de alterações
                  </button>
                  <button onClick={() => setDecision(null)} className="h-10 px-3 rounded-md text-sm text-muted-foreground hover:text-foreground">Cancelar</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const Kv = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">{label}</div>
    <div className="text-sm mt-0.5 font-medium">{value ?? "—"}</div>
  </div>
);

const NumInput = ({ label, v, onChange, testid }) => (
  <div>
    <label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">{label}</label>
    <input
      type="number" step="0.01" min="0"
      value={v ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
      className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm mt-1"
      data-testid={testid}
    />
  </div>
);
